import axios from 'axios';
import { API_URL } from '../config';
import { productService } from './productService';
import { Product } from '../types';

const getAuthToken = () => {
  const userInfo = localStorage.getItem('userInfo');
  return userInfo ? JSON.parse(userInfo).token : null;
};

const axiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const userType = user.role;

// Add token to all requests
axiosInstance.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export interface OrderItem {
  product: string | Product;
  name: string;
  quantity: number;
  price: number;
  image: string;
  lotSize?: number;
}

export interface ShippingAddress {
  address: string;
  city: string;
  postalCode: string;
  country: string;
}

export interface Order {
  _id: string;
  user: string;
  orderItems: OrderItem[];
  personalInfo: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  };
  shippingAddress: ShippingAddress;
  deliveryMethod: string;
  paymentMethod: string;
  deliveryZone?: {
    zone?: string;
    name: string;
    price: number;
  };
  assignedLivreur?: {
    user?: string;
    name: string;
    assignedAt?: Date;
  };
  itemsPrice: number;
  taxPrice: number;
  shippingPrice: number;
  totalPrice: number;
  isPaid: boolean;
  paidAt?: Date;
  isDelivered: boolean;
  deliveredAt?: Date;
  createdAt: Date;
  updatedAt: Date;
  status: 'pending' | 'processing' | 'delivered' | 'cancelled';
}

export interface OrderData {
  orderItems: Array<{
    product: string;
    name: string;
    quantity: number;
    price: number;
    image: string;
  }>;
  personalInfo: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  };
  deliveryMethod: 'pickup' | 'delivery';
  shippingAddress?: {
    street: string;
    city: string;
    postalCode: string;
    country: string;
  };
  paymentMethod: string;
  itemsPrice: number;
  shippingPrice: number;
  taxPrice?: number;
  totalPrice: number;
  zoneId?: string;
  deliveryZone?: {
    zone?: string;
    name: string;
    price: number;
  };
}

export const createOrder = async (orderData: OrderData) => {
  try {
    // Ne pas envoyer le token si absent : permet aux clients invités (sans compte) de passer commande
    const token = localStorage.getItem('token');
    const config = token
      ? { headers: { Authorization: `Bearer ${token}` } }
      : {};
    const response = await axiosInstance.post('/orders', orderData, config);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (!error.response) {
        throw new Error('Impossible de se connecter au serveur. Veuillez vérifier votre connexion internet.');
      }
    }
    throw error;
  }
};

export const getOrderById = async (orderId: string) => {
  const response = await axiosInstance.get(`/orders/${orderId}`,{headers: {
    Authorization: `Bearer ${localStorage.getItem('token')}`
  }});
  return response;
};

export const getMyOrders = async () => {
  const response = await axiosInstance.get('/orders/myorders',{headers: {
    Authorization: `Bearer ${localStorage.getItem('token')}`
  }});
  return response.data;
};

export const updateOrderToPaid = async (orderId: string, paymentResult: any) => {
  const response = await axiosInstance.put(`/orders/${orderId}/pay`, paymentResult,{headers: {
    Authorization: `Bearer ${localStorage.getItem('token')}`
  }});
  return response.data;
};

export const payOrder = async (id: string, paymentResult: {
  id: string;
  status: string;
  update_time: string;
  email_address: string;
}) => {
  const response = await axiosInstance.put(`/orders/${id}/pay`, paymentResult,{headers: {
    Authorization: `Bearer ${localStorage.getItem('token')}`
  }});
  return response.data;
};

export const deliverOrder = async (id: string) => {
  const response = await axiosInstance.put(`/orders/${id}/deliver`, {}, {
    headers: {
      Authorization: `Bearer ${localStorage.getItem('token')}`
    }
  });
  return response.data;
};

export const markOrderAsPaid = async (id: string, paymentMethod?: string) => {
  const body = paymentMethod ? { paymentMethod } : {};
  const response = await axiosInstance.put(`/orders/${id}/pay`, body, {
    headers: {
      Authorization: `Bearer ${localStorage.getItem('token')}`
    }
  });
  return response.data;
};

export interface GetAllOrdersParams {
  page?: number;
  limit?: number;
  startDate?: string;
  endDate?: string;
  deliveryOnly?: boolean;
  zoneIds?: string[];
}

export interface GetAllOrdersResponse {
  orders: Order[];
  total: number;
  page: number;
  pages: number;
}

export const getAllOrders = async (params?: GetAllOrdersParams): Promise<GetAllOrdersResponse> => {
  const response = await axiosInstance.get('/orders', {
    params: params ? {
      page: params.page,
      limit: params.limit,
      startDate: params.startDate,
      endDate: params.endDate,
      deliveryOnly: params.deliveryOnly ? 'true' : undefined,
      zoneIds: params.zoneIds?.length ? params.zoneIds.join(',') : undefined,
    } : undefined,
    headers: {
      Authorization: `Bearer ${localStorage.getItem('token')}`
    }
  });
  return response.data;
};

export const getOrdersForStatistics = async (params?: {
  startDate?: string;
  endDate?: string;
  limit?: number;
}): Promise<Order[]> => {
  const response = await axiosInstance.get('/orders/stats', {
    params: params ? {
      startDate: params.startDate,
      endDate: params.endDate,
      limit: params.limit,
    } : undefined,
    headers: {
      Authorization: `Bearer ${localStorage.getItem('token')}`
    }
  });
  return response.data as Order[];
};

export const updateOrderStatus = async (id: string, status: Order['status']): Promise<Order> => {
  const response = await axiosInstance.put(`/orders/${id}/status`, { status }, {headers: {
    Authorization: `Bearer ${localStorage.getItem('token')}`
  }});
  return response.data;
}; 





export const deleteOrder = async (orderId: string) => {
  try {
    const response = await axiosInstance.delete(`/orders/${orderId}`, {headers: {
      Authorization: `Bearer ${localStorage.getItem('token')}`
    }});
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la suppression de la commande:', error);
    throw error;
  }
};

export const updateOrderZone = async (orderId: string, zoneId: string | null) => {
  const response = await axiosInstance.put(
    `/orders/${orderId}/zone`,
    { zoneId },
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`
      }
    }
  );
  return response.data;
};

export const assignOrderToLivreur = async (orderId: string) => {
  const response = await axiosInstance.put(
    `/orders/${orderId}/assign`,
    {},
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`
      }
    }
  );
  return response.data;
};

export const updateOrder = async (orderId: string, payload: {
  personalInfo?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
  };
  orderItems?: Array<{
    product: string;
    name?: string;
    quantity: number;
    price?: number;
    image?: string;
  }>;
  shippingAddress?: {
    street?: string;
    city?: string;
    postalCode?: string;
    country?: string;
  };
  paymentMethod?: string;
  zoneId?: string | null;
}) => {
  const response = await axiosInstance.put(`/orders/${orderId}`, payload, {
    headers: {
      Authorization: `Bearer ${localStorage.getItem('token')}`
    }
  });
  return response.data;
};
